import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

const AuthCtx = createContext({ user: null, loading: true, login: async () => {}, logout: async () => {}, refresh: async () => {} });

function getStoredToken() {
  try { return localStorage.getItem("rl_token"); } catch { return null; }
}
function setStoredToken(t) {
  try { if (t) localStorage.setItem("rl_token", t); else localStorage.removeItem("rl_token"); } catch {}
}

// Auto-attach Bearer as fallback (cross-site cookies sometimes blocked)
axios.interceptors.request.use((cfg) => {
  const t = getStoredToken();
  if (t && !cfg.headers.Authorization) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally { setLoading(false); }
  };

  useEffect(() => {
    // Returning from Emergent Google OAuth: AuthCallback will exchange session_id first
    if (window.location.hash?.includes("session_id=")) { setLoading(false); return; }
    refresh();
  }, []);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = (nextPath = "/paddock") => {
    const redirectUrl = window.location.origin + nextPath;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const googleExchange = async (sessionId) => {
    const { data } = await axios.post(`${API}/auth/google/session`, { session_id: sessionId });
    if (data.token) setStoredToken(data.token);
    setUser(data);
    return data;
  };

  const login = async (email, password) => {
    const { data, headers } = await axios.post(`${API}/auth/login`, { email, password });
    // Try to extract token from Set-Cookie if server sends dual, else re-fetch
    setUser(data);
    // Fetch fresh /me to grab cookies via same-origin flow
    try {
      const me = await axios.get(`${API}/auth/me`);
      setUser(me.data);
    } catch {}
    return data;
  };

  const register = async (payload) => {
    const sl = localStorage.getItem("rl_sl"), ref = localStorage.getItem("rl_ref");
    const { data } = await axios.post(`${API}/auth/register`, { ...payload, ...(sl ? { sl } : {}), ...(ref ? { ref } : {}) });
    if (sl) localStorage.removeItem("rl_sl");
    if (ref) localStorage.removeItem("rl_ref");
    setUser(data);
    try {
      const me = await axios.get(`${API}/auth/me`);
      setUser(me.data);
    } catch {}
    return data;
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    try { caches.delete("rl-promax-files"); Object.keys(localStorage).filter(k => k.startsWith("rl_promax_") && k !== "rl_promax_saved").forEach(k => localStorage.removeItem(k)); } catch {}
    setStoredToken(null);
    setUser(null);
  };

  return <AuthCtx.Provider value={{ user, loading, login, register, logout, refresh, googleLogin, googleExchange }}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);
export { API };
